import React, { useState, useEffect } from "react";
import { useLocation, useParams, Link } from "react-router-dom";
import api from "../../../utils/axios";
import Swal from "sweetalert2";
import moment from "moment";

import Sidebar from "../../components/Sidebar";
import Topbar from "../../components/Topbar";
import CustomerTabsJobFile from "../../components/CustomerTabsJobFile";
import JobWorkTable from "../../components/Table/JobWorkTable";

const uuid = () =>
  crypto.randomUUID
    ? crypto.randomUUID()
    : "id-" +
      Date.now().toString(36) +
      "-" +
      Math.random().toString(36).slice(2, 10);

const parseStepIsCollect = (step = {}) => {
  if (step.Step_IsCollect != null) return step.Step_IsCollect === 1 || step.Step_IsCollect === true;
  if (step.IsCollect != null) return step.IsCollect === 1 || step.IsCollect === true;
  if (step.is_collect != null) return step.is_collect === 1 || step.is_collect === true;
  if (step.collect_type != null) return String(step.collect_type).trim() === "collect";
  if (step.Collect_Type != null) return String(step.Collect_Type).trim() === "collect";

  const remark = String(step.Step_Remark || "").trim();
  if (!remark) return false;
  return /เก็บคืน|คืน/i.test(remark);
};

export default function JobFileJobsPage() {
  const { id } = useParams();
  const location = useLocation();

  const job = location.state?.job || null;
  const fromTempEquipment = location.state?.fromTempEquipment === true;
  const fromPage = location.state?.from || null; // { label, path }
  const fromJobId = location.state?.jobId || null;
  const fromStockHistory = location.state?.fromStockHistory === true;
  const stockOverviewLink = location.state?.stockOverviewLink || "/stock/stock-overview";
  const stockOverviewLabel = location.state?.stockOverviewLabel || "ดูสต๊อกสินค้า";
  const backLink = location.state?.backLink || null;
  const backLabel = location.state?.backLabel || null;
  const jobFileBackLink = backLink && !backLink.startsWith("/jobfile") ? backLink : (backLink || "/jobfile");

  const [header, setHeader] = useState({
    Contract_No: "",
    StartDate: "",
    EndDate: "",
  });

  const [summaryData, setSummaryData] = useState({
    CustomerName: "",
    DisplayName: "",
    Phone: "",
    ContactName: "",
    ContactPosition: "",
    ServiceAddress: "",
    LastServiceRound: "",
    LastServiceDate: "",
  });

  const [customerData, setCustomerData] = useState({
    CustomerType: "",
    TaxNo: "",
    ContractEndDate: "",
    Quote_No: "",
  });

  const [works, setWorks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [contractData, setContractData] = useState(null);
  const [contractHeader, setContractHeader] = useState(null);
  const [contractServices, setContractServices] = useState([]); // CT_D details (Item_Id = Pack_Id)
  // Store status mapping for rounds (Round_Id -> status) to preserve "งานเสริม" status
  // Load from localStorage on mount
  const loadStatusMap = () => {
    try {
      const stored = localStorage.getItem(`roundStatusMap_${id}`);
      if (stored) {
        return new Map(Object.entries(JSON.parse(stored)));
      }
    } catch (error) {
      console.error("Error loading status map from localStorage:", error);
    }
    return new Map();
  };
  const [roundStatusMap, setRoundStatusMap] = useState(loadStatusMap);
  const [tempEquipment, setTempEquipment] = useState([]);
  const [assignedRoundMap, setAssignedRoundMap] = useState({});

  useEffect(() => {
    if (id) {
      fetchContractData();
    }
  }, [id]);

  const fetchContractData = async () => {
    try {
      setLoading(true);
      
      // เก็บงานเสริมที่ยังไม่ได้บันทึก (isDB = false) ไว้ก่อน
      const unsavedJobs = works.filter((w) => !w.isDB);
      
      const res = await api.get(`/get-contract/${id}`);
      
      if (res.data.status !== "OK") {
        throw new Error(res.data.message || "ไม่สามารถดึงข้อมูลได้");
      }

      const h = res.data.header || {};
      const d = res.data.details || []; // CT_D details (Item_Id = Pack_Id)
      const serviceDoneStatuses = new Set([
        "Check in ทำงาน",
        "working",
        "On-Site",
        "ปิดงาน รอเซ็น",
        "ปิดงาน เซ็นแล้ว",
        "ปิดงาน",
        "รอส่งงาน",
        "ส่งงาน",
        "ส่งงาน (Checkout)",
        "สำเร็จ",
      ]);
      
      setHeader({
        Contract_No: h.Doc_No || "",
        StartDate: h.Ct_Start_Date?.split("T")[0] || "",
        EndDate: h.Ct_End_Date?.split("T")[0] || "",
      });

      // Store contract data for saving works
      setContractData({
        Doc_Id: h.Doc_Id || id,
        Comp_Id: h.Comp_Id,
        Doc_Status: h.Doc_Status,
      });

      // Store full header for API update
      setContractHeader(h);

      // Store contract services (CT_D) - Item_Id = Pack_Id
      const services = d.map((item) => ({
        id: item.Item_Id, // Pack_Id
        code: item.Item_Code,
        name: item.Item_Name,
      }));
      console.log("Setting contractServices:", services);
      console.log("contractServices count:", services.length);
      setContractServices(services);

      // ดึงข้อมูลสรุป: ชื่อลูกค้า, เบอร์โทร, ผู้ติดต่อ, ที่อยู่, บริการครั้งแรก
      // ใช้ข้อมูลจาก header (h) ที่มาจาก /get-contract โดยตรง
      let titles = [];

      // ดึง titles สำหรับ format ชื่อลูกค้า
      try {
        const titleRes = await api.get("/get-title");
        titles = titleRes.data?.result || [];
      } catch (err) {
        console.error("Error fetching titles:", err);
      }

      // สร้างชื่อลูกค้าแบบเต็ม (ใช้ข้อมูลจาก header)
      let title = "";
      if (h.AR_TitleCode && titles.length > 0) {
        const titleObj = titles.find((t) => Number(t.Title_Id) === Number(h.AR_TitleCode));
        title = titleObj ? titleObj.Title_Name : "";
      }
      const name = h.AR_Name || "";
      const lastName = h.AR_LastName || "";
      const fullName = `${title ? title + " " : ""}${name}${lastName ? " " + lastName : ""}`.trim() || "-";

      // สร้างที่อยู่ให้บริการแบบเต็ม
      const serviceLocationParts = [
        h.AddrNo,
        h.Village,
        h.Moo ? `หมู่ ${h.Moo}` : null,
        h.Soi ? `ซอย ${h.Soi}` : null,
        h.Road ? `ถนน ${h.Road}` : null,
      ].filter(Boolean);

      const serviceAddressFull = [
        serviceLocationParts.join(" "),
        h.SubDistrictName ? `ต.${h.SubDistrictName}` : null,
        h.DistrictName ? `อ.${h.DistrictName}` : null,
        h.ProvinceName ? `จ.${h.ProvinceName}` : null,
        h.Zipcode || null,
      ].filter(Boolean).join(" ") || "-";

      // แสดงบริการครั้งล่าสุดเฉพาะรอบที่มีการเข้าบริการจริงเท่านั้น
      let lastService = null;

      // ใช้ข้อมูลผู้ติดต่อจาก header (ที่มาจาก /get-contract)
      let contactName = h.AR_ContactName;
      
      // ถ้าไม่มีชื่อผู้ติดต่อและเป็นบุคคลธรรมดา (AR_Group = 1) ให้ใช้ชื่อลูกค้าแทน
      if (!contactName && h.AR_Group === 1) {
        contactName = fullName;
      }
      
      // ถ้ายังไม่มีให้ใช้ "-"
      contactName = contactName || "-";
      
      const contactPosition = h.AR_ContactPosition || "-";

      setSummaryData({
        CustomerName: fullName,
        DisplayName: h.DisplayName || "-",
        Phone: h.AR_Tel || "-",
        ContactName: contactName,
        ContactPosition: contactPosition,
        ServiceAddress: serviceAddressFull,
        LastServiceRound: "-",
        LastServiceDate: "-",
      });

      // ดึงข้อมูลลูกค้าเพิ่มเติมสำหรับ card ข้อมูลลูกค้า
      let customerInfo = {};
      if (h.AR_Id) {
        try {
          const custRes = await api.get(`/get-tb-set-ar1/${h.AR_Id}`);
          const customerDataArray = custRes.data?.result?.recordset || [];
          if (customerDataArray.length > 0) {
            customerInfo = customerDataArray[0];
          }
        } catch (err) {
          console.error("Error fetching customer info:", err);
        }
      }

      // Format เลขผู้เสียภาษี
      const formatTaxNo = (taxNo) => {
        if (!taxNo) return "-";
        return taxNo.replace(/^(\d{1})(\d{4})(\d{5})(\d{2})(\d{1})$/, "$1-$2-$3-$4-$5");
      };

      // Format วันที่หมดสัญญา
      const formatContractEndDate = (date) => {
        if (!date) return "-";
        return moment(date).add(543, "year").format("MMMM YY");
      };

      // กำหนดประเภทลูกค้า
      const arGroup = Number(customerInfo.AR_Group) || Number(h.AR_Group) || null;
      const customerType = arGroup === 1 ? "บุคคลธรรมดา" : arGroup === 2 ? "นิติบุคคล" : "-";

      setCustomerData({
        CustomerType: customerType,
        TaxNo: formatTaxNo(customerInfo.AR_TaxNo || h.AR_TaxNo),
        ContractEndDate: formatContractEndDate(h.Contract_EndDate || customerInfo.Contract_EndDate),
        Quote_No: h.Ref_DocNo || "-",
        Quote_Id: h.Ref_DocID || null,
        Contract_Id: h.Doc_Id || id,
      });

      // Transform backend works format to frontend format
      const backendWorks = res.data.works || [];
      
      // Preserve existing works status when reloading
      // Use current works state and roundStatusMap to preserve status
      const existingWorksMap = new Map();
      works.forEach((w) => {
        if (w.Round_Id) {
          // Map by Round_Id for saved rounds
          existingWorksMap.set(w.Round_Id, w.status);
        } else if (!w.isDB) {
          // For new jobs (isDB = false), map by no and date
          const key = `${w.no}-${w.date}`;
          existingWorksMap.set(key, w.status);
        }
      });
      
      // Also check roundStatusMap (persistent status storage)
      // Load from localStorage first, then merge with current state
      let statusMap = loadStatusMap();
      // Merge with current state (in case there are unsaved changes)
      roundStatusMap.forEach((value, key) => {
        statusMap.set(key, value);
      });
      
      const formattedWorks = backendWorks.map((round) => {
        const roundDate = round.Round_Date ? round.Round_Date.split("T")[0] : "";

        // Use Round_Type from DB as source of truth
        const roundStatus = round.Round_Type === "งานเสริม" ? "งานเสริม" : "ปกติ";

        return {
          id: uuid(),
          no: round.Round_No || 1,
          Round_Id: round.Round_Id || null,
          status: roundStatus,
          date: roundDate,
          isDB: true, // All rounds from API have Round_Id, so isDB = true
          services: (round.Services || []).map((service) => ({
            id: uuid(),
            name: service.Service_Name || "",
            steps: (service.Steps || []).map((step) => ({
              id: uuid(),
              title: step.Step_Title || "",
              qty: step.Step_Qty || "",
              unit: step.Step_Unit || "",
              itemId:
                step.Step_Item_Id ??
                step.Item_Id ??
                step.item_id ??
                null,
              itemGroup:
                step.Step_Item_Group ??
                step.Item_Group ??
                step.item_group ??
                null,
              itemName:
                step.Step_Item_Name ||
                step.Item_Name ||
                step.item_name ||
                "",
              material:
                step.Step_Item_Name ||
                step.Item_Name ||
                step.item_name ||
                "",
              remark: step.Step_Remark || "",
              isCollect: parseStepIsCollect(step),
              checked: step.Step_Checked === 1 || step.Step_Checked === true,
              refStepId: step.Ref_Step_Id || null,
            })),
          })),
        };
      });

      // Update persistent status map
      setRoundStatusMap(statusMap);
      
      // Save status map to localStorage for persistence after refresh
      const statusMapObj = Object.fromEntries(statusMap);
      localStorage.setItem(`roundStatusMap_${id}`, JSON.stringify(statusMapObj));

      // Merge with new jobs that haven't been saved yet (isDB = false)
      // But exclude jobs that have been saved (have Round_Id matching with formattedWorks)
      const savedRoundIds = new Set(formattedWorks.map(w => w.Round_Id).filter(Boolean));
      const savedRoundKeys = new Set(
        formattedWorks.map(w => `${w.no}-${w.date}`).filter(Boolean)
      );
      
      // ใช้ unsavedJobs ที่เก็บไว้ก่อน fetchContractData แทน works.filter
      const newJobs = unsavedJobs.filter((w) => {
        if (w.isDB) return false; // Already saved
        // Check if this job was just saved (by matching no and date)
        const key = `${w.no}-${w.date}`;
        return !savedRoundKeys.has(key);
      });
      
      const allWorks = [...formattedWorks, ...newJobs];
      allWorks.sort((a, b) => {
        const da = a.date || "";
        const db = b.date || "";
        if (!da && !db) return 0;
        if (!da) return 1;
        if (!db) return -1;
        if (da < db) return -1;
        if (da > db) return 1;
        return 0;
      });
      const normalizedWorks = allWorks.map((work, index) => ({
        ...work,
        no: index + 1,
      }));

      setWorks(normalizedWorks);

      try {
        const compId = localStorage.getItem("company");
        const assignedRes = await api.get("/get-assigned-jobs", {
          params: {
            ...(compId ? { comp_id: compId } : {}),
            job_id: id,
          },
        });
        if (assignedRes.data?.status === "OK" && Array.isArray(assignedRes.data.result)) {
          const assignedRows = assignedRes.data.result.filter(
            (a) => String(a.JobId) === String(id)
          );

          // อ้างอิง "บริการครั้งล่าสุด" จากงานที่มีการทำบริการแล้ว (ไม่ใช่แค่ลงงาน)
          const latestServiced = assignedRows
            .filter((a) => serviceDoneStatuses.has(String(a.jobStatus || "").trim()))
            .sort((a, b) => {
              const aDate = new Date(a.Job_Date || a.Round_Date || 0).getTime();
              const bDate = new Date(b.Job_Date || b.Round_Date || 0).getTime();
              return bDate - aDate;
            })[0];
          if (latestServiced) {
            setSummaryData((prev) => ({
              ...prev,
              LastServiceRound: latestServiced.Round_No
                ? `ครั้งที่ ${latestServiced.Round_No}`
                : prev.LastServiceRound,
              LastServiceDate:
                latestServiced.Job_Date || latestServiced.Round_Date
                  ? moment(latestServiced.Job_Date || latestServiced.Round_Date).format("DD/MM/YYYY")
                  : prev.LastServiceDate,
            }));
          }

          const mapped = {};
          assignedRows.forEach((a) => {
              if (a.Round_Id != null) {
                mapped[`id:${a.Round_Id}`] = a;
              }
              if (a.Round_No != null) {
                mapped[`no:${a.Round_No}`] = a;
              }
            });
          setAssignedRoundMap(mapped);
        } else {
          setAssignedRoundMap({});
        }
      } catch (error) {
        console.error("Error fetching assigned round map:", error);
        setAssignedRoundMap({});
      }

      try {
        const teRes = await api.get(`/app/admin/temp-equipment?contract_id=${id}`);
        if (teRes.data?.status === "OK") setTempEquipment(teRes.data.result || []);
        else setTempEquipment([]);
      } catch {
        setTempEquipment([]);
      }
    } catch (error) {
      console.error("Error fetching contract data:", error);
      Swal.fire({
        icon: "error",
        title: "เกิดข้อผิดพลาด",
        text: error.message || "ไม่สามารถดึงข้อมูลสัญญาได้",
      });
      setTempEquipment([]);
    } finally {
      setLoading(false);
    }
  };

  // Convert frontend works format to backend format
  const convertWorksToBackendFormat = (frontendWorks) => {
    // Sort by Round_No to ensure proper ordering
    const sortedWorks = [...frontendWorks].sort((a, b) => (a.no || 0) - (b.no || 0));
    
    // Ensure Round_No is unique and sequential
    const usedRoundNos = new Set();
    let nextRoundNo = 1;
    
    return sortedWorks.map((round) => {
      let roundNo = round.no || nextRoundNo;
      
      // If Round_No is already used, find next available number
      while (usedRoundNos.has(roundNo)) {
        roundNo = nextRoundNo;
        nextRoundNo++;
      }
      
      usedRoundNos.add(roundNo);
      if (roundNo >= nextRoundNo) {
        nextRoundNo = roundNo + 1;
      }
      
      return {
        Round_No: roundNo,
        Round_Date: round.date || null,
        Round_Type: round.status === "งานเสริม" ? "งานเสริม" : null,
        Services: (round.services || []).map((service) => ({
          Service_Name: service.name || "",
          Steps: (service.steps || []).map((step) => ({
            Step_Title: step.title || "",
            Step_Qty: step.qty != null && step.qty !== "" ? String(step.qty) : "",
            Step_Unit: step.unit != null && step.unit !== "" ? String(step.unit) : "",
            Step_Item_Id:
              step.itemId != null && step.itemId !== "" ? step.itemId : null,
            Step_Item_Group:
              step.itemGroup != null && step.itemGroup !== "" ? step.itemGroup : null,
            Step_Item_Name:
              step.itemName ||
              step.material ||
              "",
            Step_Remark: step.remark != null && step.remark !== "" ? String(step.remark) : "",
            Step_IsCollect: step.isCollect === true ? 1 : 0,
            Step_Checked: step.checked === true || step.checked === 1 ? 1 : 0,
            Ref_Step_Id: step.refStepId || null,
          })),
        })),
      };
    });
  };

  // Save works to API
  const saveWorks = async (updatedWorks) => {

    if (!contractData || !contractData.Doc_Id) {
      const errorMsg = "ไม่พบข้อมูลสัญญา";
      console.error("❌", errorMsg);
      Swal.fire({
        icon: "error",
        title: "เกิดข้อผิดพลาด",
        text: errorMsg,
      });
      throw new Error(errorMsg);
    }

    try {
      const emp_id = Number(localStorage.getItem("emp_id")) || 1;
      const emp_name = localStorage.getItem("name") || "Admin";

      // Check if there are new jobs that haven't been saved yet (isDB = false)
      const hasNewJobs = updatedWorks.some((round) => !round.isDB);

      // Convert to backend format
      const backendWorks = convertWorksToBackendFormat(updatedWorks);

      // Prepare payload - include all required header fields from existing contract
      const payload = {
        header: {
          Doc_Status: 4, // Must be 4 to save works
          Comp_Id: contractData.Comp_Id,
          AR_Id: contractHeader?.AR_Id || contractHeader?.AR_ID, // Required field
          ServiceAddr_Id: contractHeader?.ServiceAddr_Id,
          Sale_Emp_Id: contractHeader?.Sale_Emp_Id,
          Ref_DocID: contractHeader?.Ref_DocID,
          Ref_DocNo: contractHeader?.Ref_DocNo,
          Doc_Date: contractHeader?.Doc_Date,
          Start_Date: contractHeader?.Ct_Start_Date,
          Ct_End_Date: contractHeader?.Ct_End_Date,
          Language: contractHeader?.Contract_Lang || "TH",
          Total_Price: contractHeader?.Total_Price || 0,
          Discount_Value: contractHeader?.Discount_Value || 0,
          IsVat: contractHeader?.IsVat || 0,
          Sign_Name_More: contractHeader?.Sign_Name_More,
          Service_Remark: contractHeader?.Service_Remark,
          Sign_Password: contractHeader?.Sign_Password,
          Update_By_Id: emp_id,
          Update_By_Name: emp_name,
        },
        works: backendWorks,
      };

      const res = await api.put(`/update-contract/${contractData.Doc_Id}`, payload);

      if (res.data.status === "OK") {
        // Store status mapping (by Round_No and date, and Round_Id if available)
        const newStatusMap = new Map(roundStatusMap);
        
        updatedWorks.forEach((round) => {
          if (round.status === "งานเสริม") {
            // Store by Round_No and date
            const key = `${round.no}-${round.date}`;
            newStatusMap.set(key, "งานเสริม");
            
            // If round has Round_Id, also store by Round_Id
            if (round.Round_Id) {
              newStatusMap.set(round.Round_Id, "งานเสริม");
            }
          }
        });
        setRoundStatusMap(newStatusMap);
        
        // Save to localStorage
        const statusMapObj = Object.fromEntries(newStatusMap);
        localStorage.setItem(`roundStatusMap_${id}`, JSON.stringify(statusMapObj));
        
        // Only reload if there are new jobs that were just saved
        // For inline edits (checkbox, qty, unit, remark), don't reload to avoid flickering
        if (hasNewJobs) {
          // Reload to get new Round_Id for saved jobs
          // Status will be preserved via roundStatusMap matching
          await fetchContractData();
        }
        // For inline edits, no need to reload - state is already updated
        // This prevents UI flickering when checking/unchecking or editing fields
      } else {
        throw new Error(res.data.message || "บันทึกไม่สำเร็จ");
      }
    } catch (error) {
      console.error("Error saving works:", error);
      // Don't show Swal here - let the caller handle it
      // This allows addStepsFromModal to show its own error notification
      throw error; // Re-throw error so caller knows it failed
    }
  };

  if (loading) {
    return (
      <div className="flex">
        <Sidebar />
        <div
          className="flex-1 min-h-screen bg-gray-50"
          style={{ marginLeft: "var(--sidebar-width)" }}
        >
          <div className="flex items-center justify-center h-screen">
            <div className="text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#E13F57]"></div>
              <p className="mt-2 text-gray-600">กำลังโหลดข้อมูล...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex">
      <Sidebar />

      <div
        className="flex-1 min-h-screen bg-gray-50"
        style={{ marginLeft: "var(--sidebar-width)" }}
      >
        <Topbar
          breadcrumbs={
            fromTempEquipment
              ? [
                  { label: "งานช่าง", link: "/job-management" },
                  { label: "รายการอุปกรณ์ชั่วคราว", link: "/temp-equipment" },
                  { label: header.Contract_No || job?.Contract_No || id },
                ]
              : fromPage
              ? [
                  { label: "ปฏิทินงานช่าง", link: "/job/calendar" },
                  ...(fromJobId ? [{ label: `งาน ${fromJobId}`, link: fromPage.path }] : []),
                  { label: fromPage.label, link: fromPage.path },
                  { label: header.Contract_No || id },
                ]
              : fromStockHistory
              ? [
                  { label: "สต๊อกสินค้า", link: "/stock/stock-overview" },
                  { label: stockOverviewLabel, link: stockOverviewLink },
                  { label: backLabel || "ประวัติการเคลื่อนไหวสินค้า", link: backLink },
                  { label: "รายละเอียดแฟ้มงาน", link: `/jobfile/${id}`, state: location.state },
                  { label: header.Contract_No || id },
                ]
              : [
                  { label: "งานช่าง", link: "/job-management" },
                  { label: "แฟ้มงาน", link: jobFileBackLink },
                  { label: "รายละเอียดแฟ้มงาน", link: `/jobfile/${id}` },
                  { label: header.Contract_No },
                ]
          }
        />

        <div className="sticky top-14 z-20 bg-white pl-0 pr-6">
          <CustomerTabsJobFile job={header} className="pl-2 pr-6 py-6" />
        </div>

        <div className="pl-2 pr-8 pt-3 pb-4">
          <h1 className="text-xl font-bold text-gray-900">
            รายละเอียดจ๊อบงาน – {header.Contract_No}
          </h1>
        </div>

        {/* --- SUMMARY INFO (บนสุด) --- */}
        <div className="pl-2 pr-8 pb-4">
          <div className="bg-white rounded-lg shadow-sm border p-6">
            {/* แถวที่ 1: ชื่อลูกค้า | ผู้ติดต่อ | บริการครั้งล่าสุด */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="flex min-w-0">
                <span className="text-[13px] font-bold text-gray-600 flex-shrink-0">ชื่อลูกค้า :</span>
                <span className="text-[14px] text-gray-800 ml-2 break-words">{summaryData.CustomerName || "-"}</span>
              </div>
              <div className="flex min-w-0">
                <span className="text-[13px] font-bold text-gray-600 flex-shrink-0">ผู้ติดต่อ :</span>
                <span className="text-[14px] text-gray-800 ml-2 break-words">
                  {summaryData.ContactName && summaryData.ContactName !== "-" 
                    ? `${summaryData.ContactName}${summaryData.ContactPosition && summaryData.ContactPosition !== "-" ? ` (${summaryData.ContactPosition})` : ""}`
                    : "-"}
                </span>
              </div>
              <div className="flex min-w-0">
                <span className="text-[13px] font-bold text-gray-600 flex-shrink-0">บริการครั้งล่าสุด :</span>
                <span className="text-[14px] text-gray-800 ml-2 break-words">
                  {summaryData.LastServiceRound && summaryData.LastServiceRound !== "-"
                    ? `${summaryData.LastServiceRound}${summaryData.LastServiceDate && summaryData.LastServiceDate !== "-" ? ` (${summaryData.LastServiceDate})` : ""}`
                    : "-"}
                </span>
              </div>
            </div>

            {/* แถวที่ 2: ชื่อในสัญญา | เบอร์โทร */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div className="flex min-w-0">
                <span className="text-[13px] font-bold text-gray-600 flex-shrink-0">ชื่อในสัญญา :</span>
                <span className="text-[14px] text-gray-800 ml-2 break-words">{summaryData.DisplayName || "-"}</span>
              </div>
              <div className="flex min-w-0">
                <span className="text-[13px] font-bold text-gray-600 flex-shrink-0">เบอร์โทร :</span>
                <span className="text-[14px] text-gray-800 ml-2 break-words">{summaryData.Phone || "-"}</span>
              </div>
              <div></div>
            </div>

            {/* แถวที่ 3: ที่อยู่ให้บริการ (full width) */}
            <div className="flex mb-4">
              <span className="text-[13px] font-bold text-gray-600 flex-shrink-0">ที่อยู่ให้บริการ :</span>
              <span className="text-[14px] text-gray-800 ml-2">{summaryData.ServiceAddress || "-"}</span>
            </div>

            {/* แถวที่ 4: ใบเสนอราคา */}
            <div className="flex">
              <span className="text-[13px] font-bold text-gray-600 flex-shrink-0">ใบเสนอราคา :</span>
              {customerData.Quote_Id ? (
                <Link 
                  to={`/quote/edit/${customerData.Quote_Id}`}
                  state={{
                    backLink: location.pathname + location.search,
                    backLabel: header.Contract_No || "แฟ้มงาน",
                    fromJobFile: true,
                    jobFileBackLink:
                      location.state?.backLink && String(location.state.backLink).startsWith("/jobfile")
                        ? location.state.backLink
                        : "/jobfile",
                    jobFileContractLabel: header.Contract_No || id,
                    jobFileContractLink: location.pathname + location.search,
                  }}
                  className="text-[14px] text-blue-600 hover:text-blue-800 hover:underline ml-2"
                >
                  {customerData.Quote_No || "-"}
                </Link>
              ) : (
                <span className="text-[14px] text-gray-800 ml-2">{customerData.Quote_No || "-"}</span>
              )}
            </div>
          </div>
        </div>


        <div className="pl-2 pr-8 pb-20">
          <JobWorkTable
            works={works}
            setWorks={setWorks}
            startDate={header.StartDate}
            endDate={header.EndDate}
            onSaveWorks={saveWorks}
            contractServices={contractServices}
            contractId={id}
            tempEquipment={tempEquipment}
            assignedRoundMap={assignedRoundMap}
            onAssigned={fetchContractData}
          />
        </div>
      </div>
    </div>
  );
}
